import { FiltersPanel, FilterField, Filter, FilterTabs, Input, Icon } from '@chikara/ui';
import type { FilterTabItem } from '@chikara/ui';
import type { TeamFilters as TeamFiltersState } from '@/features/team/types/team.types';
import { TEAM_ALL_VALUE } from '@/features/team/constants/team.constants';
import { hasAnyTeamFilter } from '@/features/team/utils/teamFilters.utils';
import { t } from '@/i18n';
import styles from './TeamFilters.module.css';

export interface TeamFilterOption {
  label: string;
  value: string;
}

export interface TeamFiltersProps {
  /** Pestañas por estatus (con conteo opcional). Se reciben por props. */
  tabs: FilterTabItem[];
  /** Opciones del select de Rol (catálogo). Se reciben por props. */
  roleOptions: TeamFilterOption[];
  /** Opciones del select de Estado (catálogo). Se reciben por props. */
  statusOptions: TeamFilterOption[];
  /** Borrador en edición (lo que el usuario ve en los controles). */
  draftFilters: TeamFiltersState;
  /** Actualiza el borrador con un patch parcial. */
  onFilterChange: (patch: Partial<TeamFiltersState>) => void;
  /** Estatus aplicado (pestaña activa); `all` = todas. */
  activeStatus: string;
  /** Cambia la pestaña de estatus (aplica de inmediato). */
  onStatusTabChange: (value: string) => void;
  onApply: () => void;
  onClear: () => void;
  isApplying?: boolean;
  /** Si se provee, se muestra "Descargar Excel" (seam BF-620). */
  onExportExcel?: () => void | Promise<void>;
  /** Si se provee, el menú "Descargar" ofrece PDF (export de BE, BF-641). */
  onExportPdf?: () => void | Promise<void>;
  hasResults?: boolean;
  className?: string;
}

export function TeamFilters({
  tabs,
  roleOptions,
  statusOptions,
  draftFilters,
  onFilterChange,
  activeStatus,
  onStatusTabChange,
  onApply,
  onClear,
  isApplying,
  onExportExcel,
  onExportPdf,
  hasResults,
  className,
}: TeamFiltersProps) {
  const classes = [styles.wrapper, className].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      <FilterTabs tabs={tabs} activeTab={activeStatus} onTabChange={onStatusTabChange} />

      <FiltersPanel
        onApply={onApply}
        onClear={onClear}
        hasCriteria={hasAnyTeamFilter(draftFilters)}
        isApplying={isApplying}
        onExportExcel={onExportExcel}
        onExportPdf={onExportPdf}
        hasResults={hasResults}
        applyLabel={t('team.filters.apply')}
        clearLabel={t('team.filters.clear')}
        exportLabel={t('team.filters.download')}
      >
        <FilterField className={styles.field} label={t('team.filters.emailLabel')} htmlFor="team-filter-search">
          <Input
            id="team-filter-search"
            type="search"
            size="sm"
            leadingIcon={<Icon name="search" size="sm" color="inherit" />}
            placeholder={t('team.filters.emailPlaceholder')}
            value={draftFilters.search ?? ''}
            onChange={(event) => onFilterChange({ search: event.target.value })}
          />
        </FilterField>

        <FilterField className={styles.field} label={t('team.filters.roleLabel')} htmlFor="team-filter-role">
          <Filter
            id="team-filter-role"
            size="sm"
            width="100%"
            value={draftFilters.role ?? TEAM_ALL_VALUE}
            onChange={(value) => onFilterChange({ role: value === TEAM_ALL_VALUE ? undefined : value })}
            options={[{ label: t('team.filters.rolePlaceholder'), value: TEAM_ALL_VALUE }, ...roleOptions]}
          />
        </FilterField>

        <FilterField className={styles.field} label={t('team.filters.statusLabel')} htmlFor="team-filter-status">
          <Filter
            id="team-filter-status"
            size="sm"
            width="100%"
            value={draftFilters.status ?? TEAM_ALL_VALUE}
            onChange={(value) => onFilterChange({ status: value === TEAM_ALL_VALUE ? undefined : value })}
            options={[{ label: t('team.filters.statusPlaceholder'), value: TEAM_ALL_VALUE }, ...statusOptions]}
          />
        </FilterField>
      </FiltersPanel>
    </div>
  );
}

TeamFilters.displayName = 'TeamFilters';
